import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Modal from "react-modal";
import moment from "moment";
import "moment/locale/th";

// React-Datetime (รูปแบบเดียวกับระบบ Leave / Request.js)
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";

// Material-UI Components & Core
import Grid from "components/Grid/GridContainer.js";
import Col from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import Button from "components/CustomButtons/Button.js";

// API Service
import { KpiService } from "_services/kpi.service";

const styles = {
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "400",
    fontFamily: '"Sarabun", sans-serif',
    marginBottom: "3px",
  },
  modalOverlay: {
    backgroundColor: "rgba(0,0,0,0.6)",
    zIndex: 1050,
  },
  modalContent: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    width: "780px",
    maxHeight: "90vh",
    overflowY: "auto",
    padding: "25px",
    borderRadius: "8px",
    fontFamily: '"Sarabun", sans-serif',
  },
};
const useStyles = makeStyles(styles);

/**
 * Reusable Thai DatePicker Component (ใช้ Datetime แบบเดียวกับ Leave Request.js)
 */
const ThaiDatePicker = ({ label, value, onChange, placeholder = "เลือกวันที่", small = false }) => {
  return (
    <div style={{ marginBottom: small ? "4px" : "12px" }}>
      {label && (
        <label className="font-weight-bold" style={{ fontSize: small ? "12px" : "14px", marginBottom: "4px" }}>
          {label}
        </label>
      )}
      <div style={{ position: "relative" }}>
        <i
          className="far fa-calendar-alt text-muted"
          style={{
            position: "absolute",
            left: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            pointerEvents: "none",
            fontSize: small ? "13px" : "15px",
          }}
        />
        <Datetime
          value={moment(value).isValid() ? moment(value) : null}
          renderYear={(props, year) => <td {...props}>{year + 543}</td>}
          inputProps={{
            placeholder,
            readOnly: "readonly",
            className: `form-control ${small ? "form-control-sm" : ""}`,
            style: {
              paddingLeft: "32px",
              cursor: "pointer",
              backgroundColor: "#ffffff",
              fontSize: small ? "13px" : "14px",
            },
            value: moment(value).isValid() ? moment(value).format("D MMMM BBBB") : "",
          }}
          closeOnSelect={true}
          closeOnTab={true}
          dateFormat="D MMMM BBBB"
          timeFormat={false}
          onChange={(e) => {
            if (moment.isMoment(e)) {
              onChange(e.format("YYYY-MM-DD"));
            } else if (typeof e === "string") {
              onChange(e);
            }
          }}
        />
      </div>
    </div>
  );
};

const KpiRoundList = () => {
  const classes = useStyles();
  moment.locale("th");

  // --- States ---
  const [periods, setPeriods] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editingPeriodId, setEditingPeriodId] = useState(null);

  // Form State
  const initialFormState = {
    FiscalYear: moment().format("BBBB"),
    KpiRound: 1, // 1: 6 เดือน, 2: 12 เดือน
    PeriodName: `รอบที่ 1/${moment().format("BBBB")} (รอบ 6 เดือน)`,
    StartDate: moment().format("YYYY-MM-DD"),
    EndDate: moment().add(6, "months").format("YYYY-MM-DD"),
    SetupStartDate: moment().format("YYYY-MM-DD"),
    SetupEndDate: moment().add(15, "days").format("YYYY-MM-DD"),
    DefendStartDate: moment().add(16, "days").format("YYYY-MM-DD"),
    DefendEndDate: moment().add(30, "days").format("YYYY-MM-DD"),
    SelfAssessStartDate: moment().add(5, "months").format("YYYY-MM-DD"),
    SelfAssessEndDate: moment().add(5, "months").add(15, "days").format("YYYY-MM-DD"),
    ReviewStartDate: moment().add(5, "months").add(16, "days").format("YYYY-MM-DD"),
    ReviewEndDate: moment().add(6, "months").format("YYYY-MM-DD"),
    ManualTimelineStage: 0,
  };

  const [formData, setFormData] = useState(initialFormState);

  // Clone Form State
  const [cloneData, setCloneData] = useState({
    SourcePeriodID: "",
    TargetFiscalYear: (parseInt(moment().format("BBBB"), 10) + 1).toString(),
    TargetKpiRound: 1,
    TargetPeriodName: `รอบที่ 1/${parseInt(moment().format("BBBB"), 10) + 1} (โคลนจากปีก่อนหน้า)`,
    StartDate: moment().add(1, "years").format("YYYY-MM-DD"),
    EndDate: moment().add(1, "years").add(6, "months").format("YYYY-MM-DD"),
    SetupStartDate: moment().add(1, "years").format("YYYY-MM-DD"),
    SetupEndDate: moment().add(1, "years").add(15, "days").format("YYYY-MM-DD"),
    DefendStartDate: moment().add(1, "years").add(16, "days").format("YYYY-MM-DD"),
    DefendEndDate: moment().add(1, "years").add(30, "days").format("YYYY-MM-DD"),
    SelfAssessStartDate: moment().add(1, "years").add(5, "months").format("YYYY-MM-DD"),
    SelfAssessEndDate: moment().add(1, "years").add(5, "months").add(15, "days").format("YYYY-MM-DD"),
    ReviewStartDate: moment().add(1, "years").add(5, "months").add(16, "days").format("YYYY-MM-DD"),
    ReviewEndDate: moment().add(1, "years").add(6, "months").format("YYYY-MM-DD"),
    CopyAssignments: true,
  });

  useEffect(() => {
    Modal.setAppElement("body");
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    setIsLoading(true);
    try {
      const response = await KpiService.getAllPeriods();
      if (response && response.data && response.data.data) {
        setPeriods(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching KPI periods:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let updatedVal = value;
    if (name === "KpiRound" || name === "ManualTimelineStage") {
      updatedVal = parseInt(value, 10);
    }

    const updatedForm = { ...formData, [name]: updatedVal };

    if (name === "FiscalYear" || name === "KpiRound") {
      const roundLabel = (name === "KpiRound" ? parseInt(value, 10) : formData.KpiRound) === 1 ? "รอบ 6 เดือน" : "รอบ 12 เดือน";
      const yearLabel = name === "FiscalYear" ? value : formData.FiscalYear;
      const roundNo = name === "KpiRound" ? parseInt(value, 10) : formData.KpiRound;
      updatedForm.PeriodName = `รอบที่ ${roundNo}/${yearLabel} (${roundLabel})`;
    }

    setFormData(updatedForm);
  };

  const handleDateChange = (field, dateStr) => {
    setFormData((prev) => ({
      ...prev,
      [field]: dateStr,
    }));
  };

  const handleCloneInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCloneData({
      ...cloneData,
      [name]: type === "checkbox" ? checked : name === "TargetKpiRound" ? parseInt(value, 10) : value,
    });
  };

  const handleOpenCreateModal = () => {
    setEditingPeriodId(null);
    setFormData(initialFormState);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (period) => {
    setEditingPeriodId(period.periodID);
    setFormData({
      FiscalYear: period.fiscalYear,
      KpiRound: period.kpiRound,
      PeriodName: period.periodName,
      StartDate: moment(period.startDate).format("YYYY-MM-DD"),
      EndDate: moment(period.endDate).format("YYYY-MM-DD"),
      SetupStartDate: moment(period.setupStartDate).format("YYYY-MM-DD"),
      SetupEndDate: moment(period.setupEndDate).format("YYYY-MM-DD"),
      DefendStartDate: moment(period.defendStartDate).format("YYYY-MM-DD"),
      DefendEndDate: moment(period.defendEndDate).format("YYYY-MM-DD"),
      SelfAssessStartDate: moment(period.selfAssessStartDate).format("YYYY-MM-DD"),
      SelfAssessEndDate: moment(period.selfAssessEndDate).format("YYYY-MM-DD"),
      ReviewStartDate: moment(period.reviewStartDate).format("YYYY-MM-DD"),
      ReviewEndDate: moment(period.reviewEndDate).format("YYYY-MM-DD"),
      ManualTimelineStage: period.manualTimelineStage ?? 0,
    });
    setIsModalOpen(true);
  };

  const handleOpenCloneModal = (period) => {
    setCloneData({
      ...cloneData,
      SourcePeriodID: period.periodID,
      TargetFiscalYear: (parseInt(period.fiscalYear, 10) + 1).toString(),
      TargetKpiRound: period.kpiRound,
      TargetPeriodName: `รอบที่ ${period.kpiRound}/${parseInt(period.fiscalYear, 10) + 1} (โคลนจากรอบ ${period.fiscalYear})`,
    });
    setIsCloneModalOpen(true);
  };

  const handleSavePeriod = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const payload = {
        ...formData,
        ManualTimelineStage: parseInt(formData.ManualTimelineStage, 10) || 0,
        IsActive: true,
        IsClosed: false,
      };
      if (editingPeriodId) {
        await KpiService.updatePeriod(editingPeriodId, payload);
        alert("แก้ไขรอบการประเมินสำเร็จ!");
      } else {
        await KpiService.createPeriod(payload);
        alert("สร้างรอบการประเมินใหม่สำเร็จ!");
      }
      setIsModalOpen(false);
      fetchPeriods();
    } catch (error) {
      alert("เกิดข้อผิดพลาด: " + (error.response?.data?.errors?.[0]?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClonePeriod = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await KpiService.clonePeriod(cloneData);
      alert("โคลนรอบการประเมินและตัวชี้วัดสำเร็จ!");
      setIsCloneModalOpen(false);
      fetchPeriods();
    } catch (error) {
      alert("เกิดข้อผิดพลาดในการโคลน: " + (error.response?.data?.errors?.[0]?.message || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการลบรอบการประเมินนี้? (ตัวชี้วัดทั้งหมดในรอบนี้จะถูกลบไปด้วย)")) {
      try {
        await KpiService.deletePeriod(periodId);
        alert("ลบรอบการประเมินสำเร็จ!");
        fetchPeriods();
      } catch (error) {
        alert("เกิดข้อผิดพลาดในการลบ: " + error.message);
      }
    }
  };

  const getTimelineBadgeColor = (stage) => {
    switch (stage) {
      case 1:
        return "badge-primary";
      case 2:
        return "badge-warning";
      case 3:
        return "badge-info";
      case 4:
        return "badge-success";
      default:
        return "badge-secondary";
    }
  };

  return (
    <div>
      <Grid>
        <Col xs={12} sm={12} md={12}>
          <Card>
            <CardHeader
              color="info"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h4 className={classes.cardTitleWhite} style={{ margin: 0, fontWeight: "bold" }}>
                  <i className="fas fa-calendar-alt mr-2"></i> จัดการรอบการประเมินผลการปฏิบัติราชการ (KPI Periods)
                </h4>
                <p style={{ color: "rgba(255,255,255,0.8)", margin: "4px 0 0 0", fontSize: "14px" }}>
                  กำหนดปีงบประมาณ รอบการประเมิน (6 เดือน / 12 เดือน) และช่วงเวลาของแต่ละ Timeline
                </p>
              </div>
              <Button color="success" size="sm" onClick={handleOpenCreateModal}>
                <i className="fas fa-plus mr-1"></i> สร้างรอบใหม่
              </Button>
            </CardHeader>
            <CardBody>
              {isLoading ? (
                <div className="text-center p-4">
                  <i className="fas fa-spinner fa-spin fa-2x text-info"></i>
                  <p className="mt-2">กำลังโหลดข้อมูลรอบการประเมิน...</p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered table-hover mt-2">
                    <thead className="thead-light">
                      <tr style={{ backgroundColor: "#f8f9fa", textAlign: "center" }}>
                        <th style={{ width: "60px" }}>#</th>
                        <th style={{ width: "90px" }}>ปีงบ</th>
                        <th style={{ width: "110px" }}>รอบประเมิน</th>
                        <th>ชื่อรอบการประเมิน</th>
                        <th>ระยะเวลารอบ</th>
                        <th>Timeline ปัจจุบัน</th>
                        <th style={{ width: "90px" }}>KPIs</th>
                        <th style={{ width: "160px" }}>จัดการ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {periods.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            <i className="fas fa-info-circle mr-1"></i> ยังไม่มีข้อมูลรอบการประเมิน กรุณากด "สร้างรอบใหม่"
                          </td>
                        </tr>
                      ) : (
                        periods.map((p, index) => (
                          <tr key={p.periodID}>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center font-weight-bold">{p.fiscalYear}</td>
                            <td className="text-center">
                              {p.kpiRound === 1 ? (
                                <span className="badge badge-pill badge-info px-2 py-1">รอบ 6 เดือน</span>
                              ) : (
                                <span className="badge badge-pill badge-primary px-2 py-1">รอบ 12 เดือน</span>
                              )}
                            </td>
                            <td>
                              <strong>{p.periodName}</strong>
                              <div className="text-muted" style={{ fontSize: "12px", marginTop: "3px" }}>
                                <span>1. จัดทำ: {moment(p.setupStartDate).format("D MMM")} - {moment(p.setupEndDate).format("D MMM BB")}</span> | 
                                <span> 2. Defend: {moment(p.defendStartDate).format("D MMM")} - {moment(p.defendEndDate).format("D MMM BB")}</span> | 
                                <span> 3. ประเมิน: {moment(p.selfAssessStartDate).format("D MMM")} - {moment(p.selfAssessEndDate).format("D MMM BB")}</span>
                              </div>
                            </td>
                            <td style={{ fontSize: "13px" }}>
                              {moment(p.startDate).format("D MMM BBBB")} - {moment(p.endDate).format("D MMM BBBB")}
                            </td>
                            <td className="text-center">
                              {p.manualTimelineStage && p.manualTimelineStage > 0 ? (
                                <div>
                                  <span className={`badge ${getTimelineBadgeColor(p.currentTimelineStage)} p-2`} style={{ fontSize: "12px" }}>
                                    <i className="fas fa-flask mr-1"></i> {p.currentTimelineName}
                                  </span>
                                  <div className="text-warning mt-1" style={{ fontSize: "11px", fontWeight: "bold" }}>
                                    <i className="fas fa-vial mr-1"></i> โหมดจำลอง Manual
                                  </div>
                                </div>
                              ) : (
                                <span className={`badge ${getTimelineBadgeColor(p.currentTimelineStage)} p-2`} style={{ fontSize: "12px" }}>
                                  {p.currentTimelineName}
                                </span>
                              )}
                            </td>
                            <td className="text-center font-weight-bold text-primary">
                              {p.totalKpiCount} ข้อ
                            </td>
                            <td className="text-center">
                              <button
                                className="btn btn-sm btn-outline-info mr-1"
                                title="แก้ไขรอบ"
                                onClick={() => handleOpenEditModal(p)}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-success mr-1"
                                title="โคลนรอบไปยังปีถัดไป"
                                onClick={() => handleOpenCloneModal(p)}
                              >
                                <i className="fas fa-copy"></i>
                              </button>
                              <button
                                className="btn btn-sm btn-outline-danger"
                                title="ลบรอบ"
                                onClick={() => handleDeletePeriod(p.periodID)}
                              >
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Grid>

      {/* --- Modal สร้าง/แก้ไขรอบการประเมิน --- */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: styles.modalContent,
        }}
        contentLabel="สร้าง/แก้ไขรอบการประเมิน"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0", paddingBottom: "10px" }}>
          <h4 style={{ margin: 0, color: "#007bff", fontWeight: "bold" }}>
            <i className="fas fa-calendar-check mr-2"></i>
            {editingPeriodId ? "แก้ไขรอบการประเมิน" : "สร้างรอบการประเมินใหม่"}
          </h4>
          <button type="button" className="close" onClick={() => setIsModalOpen(false)}>
            <span>&times;</span>
          </button>
        </div>

        <form onSubmit={handleSavePeriod} className="mt-3">
          {/* ข้อมูลพื้นฐานรอบ */}
          <div className="form-row">
            <div className="form-group col-md-4">
              <label className="font-weight-bold">ปีงบประมาณ <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                name="FiscalYear"
                value={formData.FiscalYear}
                onChange={handleInputChange}
                placeholder="เช่น 2569"
                required
              />
            </div>
            <div className="form-group col-md-4">
              <label className="font-weight-bold">ประเภทรอบ <span className="text-danger">*</span></label>
              <select
                className="form-control"
                name="KpiRound"
                value={formData.KpiRound}
                onChange={handleInputChange}
              >
                <option value={1}>รอบที่ 1 (รอบ 6 เดือน)</option>
                <option value={2}>รอบที่ 2 (รอบ 12 เดือน / 1 ปี)</option>
              </select>
            </div>
            <div className="form-group col-md-4">
              <label className="font-weight-bold">ชื่อรอบการประเมิน <span className="text-danger">*</span></label>
              <input
                type="text"
                className="form-control"
                name="PeriodName"
                value={formData.PeriodName}
                onChange={handleInputChange}
                required
              />
            </div>
          </div>

          <div className="form-row">
            <div className="col-md-6">
              <ThaiDatePicker
                label="วันที่เริ่มต้นรอบการประเมิน"
                value={formData.StartDate}
                onChange={(val) => handleDateChange("StartDate", val)}
              />
            </div>
            <div className="col-md-6">
              <ThaiDatePicker
                label="วันที่สิ้นสุดรอบการประเมิน"
                value={formData.EndDate}
                onChange={(val) => handleDateChange("EndDate", val)}
              />
            </div>
          </div>

          {/* ส่วนจำลองไทม์ไลน์ (Simulation Mode / Manual Override) */}
          <div className="p-3 my-3 rounded" style={{ backgroundColor: "#f0f4ff", border: "1px solid #c7d2fe" }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="font-weight-bold mb-0" style={{ color: "#3730a3", fontSize: "14px" }}>
                <i className="fas fa-flask mr-2 text-primary"></i> โหมดจำลองสถานะ Timeline (Simulation Mode)
              </label>
              {Number(formData.ManualTimelineStage) > 0 ? (
                <span className="badge badge-warning px-2 py-1" style={{ fontSize: "12px" }}>
                  <i className="fas fa-vial mr-1"></i> โหมดจำลองเปิดอยู่ (Stage {formData.ManualTimelineStage})
                </span>
              ) : (
                <span className="badge badge-secondary px-2 py-1" style={{ fontSize: "12px" }}>
                  <i className="fas fa-clock mr-1"></i> ตามเวลาจริง (Auto by Date)
                </span>
              )}
            </div>
            <p className="text-muted mb-2" style={{ fontSize: "12px" }}>
              เลือกกำหนดสถานะ Timeline ปัจจุบันเพื่อจำลองการทำงาน/ทดสอบระบบ โดยไม่อ้างอิงวันที่จริง:
            </p>

            <div className="bg-white p-3 rounded border">
              <div className="form-check mb-2">
                <input
                  type="radio"
                  id="stage-auto"
                  name="ManualTimelineStage"
                  className="form-check-input"
                  style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  value={0}
                  checked={!formData.ManualTimelineStage || Number(formData.ManualTimelineStage) === 0}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ml-2 font-weight-bold text-secondary" htmlFor="stage-auto" style={{ cursor: "pointer", fontSize: "13px" }}>
                  ⏱️ ตามเวลาจริง (Auto) <span className="text-muted font-weight-normal" style={{ fontSize: "12px" }}>- คำนวณสถานะอัตโนมัติตามช่วงเวลา 4 Timeline ด้านล่าง</span>
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  type="radio"
                  id="stage-1"
                  name="ManualTimelineStage"
                  className="form-check-input"
                  style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  value={1}
                  checked={Number(formData.ManualTimelineStage) === 1}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ml-2" htmlFor="stage-1" style={{ cursor: "pointer", fontSize: "13px" }}>
                  <span className="badge badge-primary mr-1">Timeline 1</span>
                  <strong className="text-primary">ช่วงจัดทำตัวชี้วัด (Indicator Setup)</strong>
                  <span className="text-muted ml-1" style={{ fontSize: "12px" }}>(จำลองให้สร้าง/แก้ไข/ส่งอนุมัติ KPI ได้)</span>
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  type="radio"
                  id="stage-2"
                  name="ManualTimelineStage"
                  className="form-check-input"
                  style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  value={2}
                  checked={Number(formData.ManualTimelineStage) === 2}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ml-2" htmlFor="stage-2" style={{ cursor: "pointer", fontSize: "13px" }}>
                  <span className="badge badge-warning mr-1">Timeline 2</span>
                  <strong className="text-warning">ช่วง Defend ตัวชี้วัด / กดยอมรับข้อตกลง (Defend & Agreement)</strong>
                  <span className="text-muted ml-1" style={{ fontSize: "12px" }}>(จำลองให้เจรจาต่อรอง/ยินยอมรับข้อตกลง)</span>
                </label>
              </div>

              <div className="form-check mb-2">
                <input
                  type="radio"
                  id="stage-3"
                  name="ManualTimelineStage"
                  className="form-check-input"
                  style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  value={3}
                  checked={Number(formData.ManualTimelineStage) === 3}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ml-2" htmlFor="stage-3" style={{ cursor: "pointer", fontSize: "13px" }}>
                  <span className="badge badge-info mr-1">Timeline 3</span>
                  <strong className="text-info">ช่วงประเมินตนเอง (Self Assessment)</strong>
                  <span className="text-muted ml-1" style={{ fontSize: "12px" }}>(จำลองเปิดให้ประเมินผลงานและให้คะแนน 1-5)</span>
                </label>
              </div>

              <div className="form-check">
                <input
                  type="radio"
                  id="stage-4"
                  name="ManualTimelineStage"
                  className="form-check-input"
                  style={{ cursor: "pointer", width: "16px", height: "16px" }}
                  value={4}
                  checked={Number(formData.ManualTimelineStage) === 4}
                  onChange={handleInputChange}
                />
                <label className="form-check-label ml-2" htmlFor="stage-4" style={{ cursor: "pointer", fontSize: "13px" }}>
                  <span className="badge badge-success mr-1">Timeline 4</span>
                  <strong className="text-success">ช่วงกลั่นกรองสรุปคะแนน (Review & Final Evaluation)</strong>
                  <span className="text-muted ml-1" style={{ fontSize: "12px" }}>(จำลองช่วงผู้บังคับบัญชากลั่นกรองและสรุปผลคะแนน)</span>
                </label>
              </div>
            </div>
          </div>

          <h5 className="mt-2 mb-2 font-weight-bold text-secondary" style={{ borderBottom: "1px dashed #ccc", paddingBottom: "5px" }}>
            <i className="fas fa-clock mr-1"></i> กำหนดช่วงเวลา 4 Timeline ในรอบนี้
          </h5>

          {/* Timeline 1: Setup */}
          <div className="p-2 mb-2 rounded" style={{ backgroundColor: "#f0f8ff", border: "1px solid #d0e7ff" }}>
            <div className="font-weight-bold text-primary mb-1" style={{ fontSize: "13px" }}>
              1. ช่วงจัดทำตัวชี้วัด (Indicator Setup)
            </div>
            <div className="form-row">
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่เริ่ม"
                  value={formData.SetupStartDate}
                  onChange={(val) => handleDateChange("SetupStartDate", val)}
                  small={true}
                />
              </div>
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่สิ้นสุด"
                  value={formData.SetupEndDate}
                  onChange={(val) => handleDateChange("SetupEndDate", val)}
                  small={true}
                />
              </div>
            </div>
          </div>

          {/* Timeline 2: Defend */}
          <div className="p-2 mb-2 rounded" style={{ backgroundColor: "#fffdf0", border: "1px solid #ffeeba" }}>
            <div className="font-weight-bold text-warning mb-1" style={{ fontSize: "13px" }}>
              2. ช่วง Defend ตัวชี้วัด / กดยอมรับข้อตกลง (Defend & Agreement)
            </div>
            <div className="form-row">
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่เริ่ม"
                  value={formData.DefendStartDate}
                  onChange={(val) => handleDateChange("DefendStartDate", val)}
                  small={true}
                />
              </div>
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่สิ้นสุด"
                  value={formData.DefendEndDate}
                  onChange={(val) => handleDateChange("DefendEndDate", val)}
                  small={true}
                />
              </div>
            </div>
          </div>

          {/* Timeline 3: Self Assessment */}
          <div className="p-2 mb-2 rounded" style={{ backgroundColor: "#f0fff4", border: "1px solid #c3e6cb" }}>
            <div className="font-weight-bold text-success mb-1" style={{ fontSize: "13px" }}>
              3. ช่วงประเมินตนเอง (Self Assessment)
            </div>
            <div className="form-row">
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่เริ่ม"
                  value={formData.SelfAssessStartDate}
                  onChange={(val) => handleDateChange("SelfAssessStartDate", val)}
                  small={true}
                />
              </div>
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่สิ้นสุด"
                  value={formData.SelfAssessEndDate}
                  onChange={(val) => handleDateChange("SelfAssessEndDate", val)}
                  small={true}
                />
              </div>
            </div>
          </div>

          {/* Timeline 4: Review */}
          <div className="p-2 mb-3 rounded" style={{ backgroundColor: "#fdf0f0", border: "1px solid #f5c6cb" }}>
            <div className="font-weight-bold text-danger mb-1" style={{ fontSize: "13px" }}>
              4. ช่วงกลั่นกรองสรุปคะแนน (Review & Final Evaluation)
            </div>
            <div className="form-row">
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่เริ่ม"
                  value={formData.ReviewStartDate}
                  onChange={(val) => handleDateChange("ReviewStartDate", val)}
                  small={true}
                />
              </div>
              <div className="col-md-6">
                <ThaiDatePicker
                  label="วันที่สิ้นสุด"
                  value={formData.ReviewEndDate}
                  onChange={(val) => handleDateChange("ReviewEndDate", val)}
                  small={true}
                />
              </div>
            </div>
          </div>

          <div className="text-right border-top pt-3">
            <Button color="transparent" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
              ยกเลิก
            </Button>
            <Button color="primary" type="submit" disabled={isSaving}>
              {isSaving ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-save mr-1"></i>}
              {editingPeriodId ? "บันทึกการแก้ไข" : "บันทึกสร้างรอบ"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* --- Modal โคลนรอบการประเมิน (Clone Period) --- */}
      <Modal
        isOpen={isCloneModalOpen}
        onRequestClose={() => setIsCloneModalOpen(false)}
        style={{
          overlay: styles.modalOverlay,
          content: { ...styles.modalContent, width: "650px" },
        }}
        contentLabel="โคลนรอบการประเมิน"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e0e0e0", paddingBottom: "10px" }}>
          <h4 style={{ margin: 0, color: "#28a745", fontWeight: "bold" }}>
            <i className="fas fa-copy mr-2"></i> โคลนรอบการประเมินและตัวชี้วัด (Clone)
          </h4>
          <button type="button" className="close" onClick={() => setIsCloneModalOpen(false)}>
            <span>&times;</span>
          </button>
        </div>

        <form onSubmit={handleClonePeriod} className="mt-3">
          <div className="alert alert-info py-2" style={{ fontSize: "13px" }}>
            <i className="fas fa-info-circle mr-1"></i> ระบบจะคัดลอกตัวชี้วัด (KPI Master) ทั้งหมดจากรอบเดิมมาสร้างเป็นรอบใหม่โดยอัตโนมัติ
          </div>

          <div className="form-group">
            <label className="font-weight-bold">ปีงบประมาณเป้าหมาย <span className="text-danger">*</span></label>
            <input
              type="text"
              className="form-control"
              name="TargetFiscalYear"
              value={cloneData.TargetFiscalYear}
              onChange={handleCloneInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label className="font-weight-bold">ประเภทรอบเป้าหมาย</label>
            <select
              className="form-control"
              name="TargetKpiRound"
              value={cloneData.TargetKpiRound}
              onChange={handleCloneInputChange}
            >
              <option value={1}>รอบที่ 1 (รอบ 6 เดือน)</option>
              <option value={2}>รอบที่ 2 (รอบ 12 เดือน / 1 ปี)</option>
            </select>
          </div>

          <div className="form-group">
            <label className="font-weight-bold">ชื่อรอบการประเมินใหม่</label>
            <input
              type="text"
              className="form-control"
              name="TargetPeriodName"
              value={cloneData.TargetPeriodName}
              onChange={handleCloneInputChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="col-md-6">
              <ThaiDatePicker
                label="วันที่เริ่มต้นรอบใหม่"
                value={cloneData.StartDate}
                onChange={(val) => setCloneData((prev) => ({ ...prev, StartDate: val }))}
              />
            </div>
            <div className="col-md-6">
              <ThaiDatePicker
                label="วันที่สิ้นสุดรอบใหม่"
                value={cloneData.EndDate}
                onChange={(val) => setCloneData((prev) => ({ ...prev, EndDate: val }))}
              />
            </div>
          </div>

          <div className="form-group form-check mt-3">
            <input
              type="checkbox"
              className="form-check-input"
              id="copyAssignCheck"
              name="CopyAssignments"
              checked={cloneData.CopyAssignments}
              onChange={handleCloneInputChange}
            />
            <label className="form-check-label" htmlFor="copyAssignCheck">
              คัดลอกรายชื่อผู้รับมอบหมายตัวชี้วัด (Assignees) มาด้วย
            </label>
          </div>

          <div className="text-right border-top pt-3 mt-4">
            <Button color="transparent" onClick={() => setIsCloneModalOpen(false)} disabled={isSaving}>
              ยกเลิก
            </Button>
            <Button color="success" type="submit" disabled={isSaving}>
              {isSaving ? <i className="fas fa-spinner fa-spin mr-1"></i> : <i className="fas fa-check mr-1"></i>}
              ยืนยันการโคลนรอบ
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default KpiRoundList;
